import { fetchAuthSession } from 'aws-amplify/auth';
import { env } from './env';
import { ApiError, NoSessionTokensError } from './errors';

const DEFAULT_TIMEOUT_MS = 5000;
const AUTH_TOKEN_HEADER_NAME = 'Authorization';

export const apiUrls = {
  activity: `${env.apiBaseUrl}/activity`,
  adminAccess: `${env.apiBaseUrl}/admin-access`,
  announcement: `${env.apiBaseUrl}/announcement`,
  alertSettings: `${env.apiBaseUrl}/alert-settings`,
  contact: `${env.apiBaseUrl}/contact`,
  fund: `${env.apiBaseUrl}/fund`,
  nonBusinessDay: `${env.apiBaseUrl}/non-business-day`,
  tocAcceptance: `${env.apiBaseUrl}/toc-acceptance`,
  trade: `${env.apiBaseUrl}/trade`,
} as const;

export interface RequestOptions {
  /** Appended as query string; undefined and empty values are dropped. */
  params?: Record<string, string | number | undefined>;
  body?: unknown;
  /** Human-readable name used in error messages, e.g. "retrieving funds". */
  operation: string;
  timeoutMs?: number;
  /** Send cookies. Needed for the admin-access call, which sets a signed cookie. */
  withCredentials?: boolean;
  signal?: AbortSignal;
}

function buildUrl(baseUrl: string, params?: RequestOptions['params']): string {
  if (!params) return baseUrl;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.append(key, String(value));
  }
  const query = search.toString();
  if (!query) return baseUrl;
  return baseUrl.includes('?') ? `${baseUrl}&${query}` : `${baseUrl}?${query}`;
}

function messageForStatus(status: number, fallback?: string): string {
  switch (status) {
    case 401:
      return 'You are not authorized. Please log in again.';
    case 403:
      return 'You do not have permission to access this resource.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      return 'A server error occurred. Please try again later.';
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    default:
      return fallback || `An error occurred (${status})`;
  }
}

function codeForStatus(status: number): string {
  switch (status) {
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 500:
      return 'SERVER_ERROR';
    case 503:
      return 'SERVICE_UNAVAILABLE';
    default:
      return 'HTTP_ERROR';
  }
}

export async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  if (!session.tokens?.idToken) {
    throw new NoSessionTokensError();
  }
  return session.tokens.idToken.toString();
}

/**
 * One place for what every Angular service repeated by hand: fetch the ID
 * token, attach it, bound the call with a timeout, and map failures onto
 * ApiError. Retries are deliberately NOT here; TanStack Query owns them, so
 * retry policy is configured once in queryClient.ts instead of per call site.
 */
async function request<T>(
  method: 'GET' | 'PUT' | 'POST' | 'DELETE',
  baseUrl: string,
  options: RequestOptions,
): Promise<T> {
  const { operation, timeoutMs = DEFAULT_TIMEOUT_MS, withCredentials, body, signal } = options;
  const url = buildUrl(baseUrl, options.params);

  let idToken: string;
  try {
    idToken = await getIdToken();
  } catch (error) {
    throw new ApiError(operation, 'Your session has ended. Please log in again.', 'NO_SESSION', {
      cause: error,
    });
  }

  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

  const headers: Record<string, string> = { [AUTH_TOKEN_HEADER_NAME]: idToken };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      signal: combinedSignal,
      credentials: withCredentials ? 'include' : 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if (timeoutSignal.aborted) {
      throw new ApiError(
        operation,
        'The request took too long. Please try again.',
        'TIMEOUT',
        { cause: error },
      );
    }
    if (signal?.aborted) {
      throw error; // caller cancelled; let TanStack Query treat it as a cancellation
    }
    throw new ApiError(operation, 'Could not reach the server. Check your connection.', 'NETWORK_ERROR', {
      cause: error,
    });
  }

  if (!response.ok) {
    let serverMessage: string | undefined;
    try {
      const parsed = await response.json();
      serverMessage = typeof parsed?.message === 'string' ? parsed.message : undefined;
    } catch {
      // Body wasn't JSON. The status-based message below is good enough.
    }
    throw new ApiError(
      operation,
      messageForStatus(response.status, serverMessage),
      codeForStatus(response.status),
      { status: response.status },
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;

  try {
    return JSON.parse(text) as T;
  } catch (error) {
    throw new ApiError(operation, 'The server returned an unexpected response.', 'INVALID_JSON', {
      cause: error,
    });
  }
}

export const api = {
  get: <T>(url: string, options: RequestOptions) => request<T>('GET', url, options),
  put: <T>(url: string, options: RequestOptions) => request<T>('PUT', url, options),
  post: <T>(url: string, options: RequestOptions) => request<T>('POST', url, options),
  delete: <T>(url: string, options: RequestOptions) => request<T>('DELETE', url, options),
};
